'use strict';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { formatCurrency } from '../../../utils/formatters';

// ── Tipi ─────────────────────────────────────────────────────────────────────

interface Banca { id: number; nome: string; attiva: boolean; }
interface AnteprimaRata { n: number; qi: number; qc: number; rata: number; residuo: number; }

// ── Labels ────────────────────────────────────────────────────────────────────

const TIPO_AMM_DESC: Record<string, string> = {
  FRANCESE:  'Francese — rata costante (più comune)',
  ITALIANO:  'Italiano — quota capitale costante, rata decrescente',
  AMERICANO: 'Americano — solo interessi periodici, capitale a scadenza',
  BULLET:    'Bullet — unico rimborso a scadenza',
};

const TIPO_ASSET_LABELS: Record<string, string> = {
  IMMOBILE_RESIDENZIALE: 'Immobile Residenziale',
  IMMOBILE_COMMERCIALE:  'Immobile Commerciale',
  IMMOBILE_INDUSTRIALE:  'Immobile Industriale',
  MACCHINARIO:           'Macchinario',
  VEICOLO:               'Veicolo',
  ATTREZZATURA:          'Attrezzatura',
  ALTRO:                 'Altro',
};

// ── Calcolo anteprima piano (solo prime 12 rate) ──────────────────────────────

function calcolaAnteprimaPiano(form: Record<string, any>): AnteprimaRata[] {
  const capitale = Number(form.importoOriginale) || 0;
  const tasso = Number(form.tassoIniziale) || 0;
  const durataAnni = Number(form.durataAnni) || 1;
  const cadenza = form.cadenzaRata || 'MENSILE';
  const tipo = form.tipoAmmortamento || 'FRANCESE';
  const periodi: Record<string, number> = { MENSILE: 12, TRIMESTRALE: 4, SEMESTRALE: 2, ANNUALE: 1 };
  const n = durataAnni * periodi[cadenza];
  const i = (tasso / 100) / periodi[cadenza];
  const r2 = (x: number) => Math.round(x * 100) / 100;

  if (!capitale || !n) return [];

  const rows: AnteprimaRata[] = [];
  let residuo = capitale;

  if (tipo === 'FRANCESE') {
    const rata = i > 0 ? r2(capitale * i / (1 - Math.pow(1 + i, -n))) : r2(capitale / n);
    for (let k = 0; k < Math.min(n, 12); k++) {
      const qi = r2(residuo * i);
      const qc = k === n - 1 ? r2(residuo) : r2(rata - qi);
      residuo = r2(residuo - qc);
      rows.push({ n: k + 1, qi, qc, rata: r2(qc + qi), residuo: Math.max(residuo, 0) });
    }
  } else if (tipo === 'ITALIANO') {
    const qcC = r2(capitale / n);
    for (let k = 0; k < Math.min(n, 12); k++) {
      const qi = r2(residuo * i);
      const qc = k === n - 1 ? r2(residuo) : qcC;
      residuo = r2(residuo - qc);
      rows.push({ n: k + 1, qi, qc, rata: r2(qc + qi), residuo: Math.max(residuo, 0) });
    }
  } else if (tipo === 'AMERICANO') {
    const qi = r2(capitale * i);
    for (let k = 0; k < Math.min(n, 12); k++) {
      const qc = k === n - 1 ? capitale : 0;
      rows.push({ n: k + 1, qi, qc, rata: r2(qc + qi), residuo: k === n - 1 ? 0 : capitale });
    }
  } else {
    rows.push({ n: 1, qi: r2(capitale * (tasso / 100) * durataAnni), qc: capitale, rata: r2(capitale + capitale * (tasso / 100) * durataAnni), residuo: 0 });
  }
  return rows;
}

// ── Componente ────────────────────────────────────────────────────────────────

const formInit = {
  societaId: '', bancaId: '', nomeBanca: '', bancaManuale: false,
  descrizione: '', importoOriginale: '', dataErogazione: '',
  durataAnni: '', tipoAmmortamento: 'FRANCESE', tipoTasso: 'FISSO',
  tassoIniziale: '', cadenzaRata: 'MENSILE', dataPrimaRata: '',
  haPreammortamento: false, durataPreammortamentoMesi: '',
  tipoAsset: '', descrizioneAsset: '', valoreStimatoAsset: '',
  note: '', step: 1,
};

export default function NuovoFinanziamento() {
  const navigate = useNavigate();

  const {
    societaId: filterSocietaId,
    listaSocieta: societa,
    isUnica,
    pronta,
  } = useFiltroSocieta();

  const [banche,   setBanche]   = useState<Banca[]>([]);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');
  const [form,     setForm]     = useState<Record<string, any>>(formInit);

  // Pre-popola societaId dalla società attiva/filtro
  useEffect(() => {
    if (!pronta) return;
    if (isUnica && societa.length === 1) {
      setForm(p => ({ ...p, societaId: String(societa[0].id) }));
    } else if (filterSocietaId) {
      setForm(p => ({ ...p, societaId: filterSocietaId }));
    }
  }, [pronta, isUnica, societa, filterSocietaId]);

  // Carica banche al cambio societaId nel form
  useEffect(() => {
    if (!form.societaId) { setBanche([]); return; }
    // @api GET /api/banche?societaId= -> [{ id, nome }]
    apiFetch<Banca[]>(`/api/banche?societaId=${form.societaId}`)
      .then(d => setBanche(Array.isArray(d) ? d : []))
      .catch(() => setBanche([]));
  }, [form.societaId]);

  const anteprima = useMemo(() => calcolaAnteprimaPiano(form), [form]);

  // Validazione step
  const step1Valid = !!(
    form.societaId && form.nomeBanca && form.descrizione &&
    form.importoOriginale && form.dataErogazione &&
    form.durataAnni && form.tassoIniziale && form.dataPrimaRata
  );
  const step2Valid = !form.haPreammortamento || !!form.durataPreammortamentoMesi;

  const creaFinanziamento = async () => {
    setSaving(true); setError('');
    try {
      // @api POST /api/finanziamenti {...} -> { finanziamento: { id: number, ... } }
      const res = await apiFetch<{ finanziamento: { id: number } }>('/api/finanziamenti', {
        method: 'POST',
        body: JSON.stringify({
          societaId:                 Number(form.societaId),
          bancaId:                   form.bancaManuale ? null : (form.bancaId ? Number(form.bancaId) : null),
          nomeBanca:                 form.nomeBanca,
          descrizione:               form.descrizione,
          importoOriginale:          Number(form.importoOriginale),
          dataErogazione:            form.dataErogazione,
          durataAnni:                Number(form.durataAnni),
          tipoAmmortamento:          form.tipoAmmortamento,
          tipoTasso:                 form.tipoTasso,
          tassoIniziale:             Number(form.tassoIniziale),
          cadenzaRata:               form.cadenzaRata,
          dataPrimaRata:             form.dataPrimaRata,
          haPreammortamento:         form.haPreammortamento,
          durataPreammortamentoMesi: form.haPreammortamento ? Number(form.durataPreammortamentoMesi) : null,
          tipoAsset:                 form.tipoAsset || null,
          descrizioneAsset:          form.descrizioneAsset || null,
          valoreStimatoAsset:        form.valoreStimatoAsset ? Number(form.valoreStimatoAsset) : null,
          note:                      form.note || null,
        }),
      });
      navigate(`/amministrazione/finanziamenti/${res.finanziamento.id}`);
    } catch (e: any) {
      setError(e.message || 'Errore nella creazione');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout title="Nuovo Finanziamento">
      <div className="bop-page__header">
        <div className="bop-page__header-text">
          <h2 className="bop-page__title">Nuovo Finanziamento</h2>
          <p className="bop-page__subtitle">Step {form.step} di 3</p>
        </div>
        <div className="bop-finanz__modal-footer">
          <button
            className="bop-btn bop-btn-ghost"
            onClick={() => navigate('/amministrazione/finanziamenti')}
          >
            ← Annulla
          </button>
          {form.step > 1 && (
            <button
              className="bop-btn bop-btn-ghost"
              onClick={() => setForm(p => ({ ...p, step: p.step - 1 }))}
            >
              ← Indietro
            </button>
          )}
          {form.step < 3 && (
            <button
              className="bop-btn bop-btn-primary"
              onClick={() => setForm(p => ({ ...p, step: p.step + 1 }))}
              disabled={form.step === 1 ? !step1Valid : !step2Valid}
            >
              Avanti →
            </button>
          )}
          {form.step === 3 && (
            <button
              className="bop-btn bop-btn-primary"
              onClick={creaFinanziamento}
              disabled={saving}
            >
              {saving ? 'Creazione…' : 'Crea Finanziamento'}
            </button>
          )}
        </div>
      </div>

      {/* Indicatore step */}
      <div className="bop-finanz__step-indicator">
        {[1, 2, 3].map(s => (
          <div
            key={s}
            className={`bop-finanz__step-dot${form.step === s ? ' bop-finanz__step-dot--active' : form.step > s ? ' bop-finanz__step-dot--done' : ''}`}
          >
            {s}
          </div>
        ))}
      </div>

      {error && <div className="bop-inline-error">{error}</div>}

      <div className="bop-card bop-finanz__nuovo-card">

        {/* ── STEP 1: Dati principali ── */}
        {form.step === 1 && (
          <div className="bop-finanz__vstack">
            {!isUnica && (
              <div className="bop-form-group">
                <label className="bop-label">Società *</label>
                <select
                  className="bop-select"
                  value={form.societaId}
                  onChange={e => setForm(p => ({ ...p, societaId: e.target.value, bancaId: '', nomeBanca: '' }))}
                >
                  <option value="">— Seleziona —</option>
                  {societa.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
                </select>
              </div>
            )}

            <div className="bop-form-group">
              <label className="bop-label">Banca *</label>
              <div className="bop-finanz__check-row bop-finanz__mb-2">
                <input
                  type="checkbox"
                  checked={form.bancaManuale}
                  onChange={e => setForm(p => ({ ...p, bancaManuale: e.target.checked, bancaId: '', nomeBanca: '' }))}
                />
                <span className="bop-finanz__check-label">Banca non in anagrafica (inserimento manuale)</span>
              </div>
              {form.bancaManuale ? (
                <input
                  className="bop-input"
                  placeholder="Nome banca"
                  value={form.nomeBanca}
                  onChange={e => setForm(p => ({ ...p, nomeBanca: e.target.value }))}
                />
              ) : (
                <select
                  className="bop-select"
                  value={form.bancaId}
                  onChange={e => {
                    const b = banche.find(x => String(x.id) === e.target.value);
                    setForm(p => ({ ...p, bancaId: e.target.value, nomeBanca: b?.nome ?? '' }));
                  }}
                >
                  <option value="">— Seleziona banca —</option>
                  {banche.map(b => <option key={b.id} value={b.id}>{b.nome}</option>)}
                </select>
              )}
            </div>

            <div className="bop-form-group">
              <label className="bop-label">Descrizione *</label>
              <input
                className="bop-input"
                placeholder="es. Mutuo capannone via Roma"
                value={form.descrizione}
                onChange={e => setForm(p => ({ ...p, descrizione: e.target.value }))}
              />
            </div>

            <div className="bop-finanz__form-grid-2">
              <div className="bop-form-group">
                <label className="bop-label">Importo Originale (€) *</label>
                <input
                  className="bop-input"
                  type="number"
                  placeholder="0.00"
                  value={form.importoOriginale}
                  onChange={e => setForm(p => ({ ...p, importoOriginale: e.target.value }))}
                />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Durata (anni) *</label>
                <input
                  className="bop-input"
                  type="number"
                  placeholder="10"
                  value={form.durataAnni}
                  onChange={e => setForm(p => ({ ...p, durataAnni: e.target.value }))}
                />
              </div>
            </div>

            <div className="bop-finanz__form-grid-2">
              <div className="bop-form-group">
                <label className="bop-label">Data Erogazione *</label>
                <input
                  className="bop-input"
                  type="date"
                  value={form.dataErogazione}
                  onChange={e => setForm(p => ({ ...p, dataErogazione: e.target.value }))}
                />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Data Prima Rata *</label>
                <input
                  className="bop-input"
                  type="date"
                  value={form.dataPrimaRata}
                  onChange={e => setForm(p => ({ ...p, dataPrimaRata: e.target.value }))}
                />
              </div>
            </div>

            <div className="bop-form-group">
              <label className="bop-label">Tipo Ammortamento</label>
              <select
                className="bop-select"
                value={form.tipoAmmortamento}
                onChange={e => setForm(p => ({ ...p, tipoAmmortamento: e.target.value }))}
              >
                {Object.entries(TIPO_AMM_DESC).map(([k, v]) =>
                  <option key={k} value={k}>{v}</option>
                )}
              </select>
            </div>

            <div className="bop-finanz__form-grid-3">
              <div className="bop-form-group">
                <label className="bop-label">Tipo Tasso</label>
                <select
                  className="bop-select"
                  value={form.tipoTasso}
                  onChange={e => setForm(p => ({ ...p, tipoTasso: e.target.value }))}
                >
                  <option value="FISSO">Fisso</option>
                  <option value="VARIABILE">Variabile</option>
                </select>
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Tasso % annuo *</label>
                <input
                  className="bop-input"
                  type="number"
                  step="0.01"
                  placeholder="3.50"
                  value={form.tassoIniziale}
                  onChange={e => setForm(p => ({ ...p, tassoIniziale: e.target.value }))}
                />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Cadenza Rate</label>
                <select
                  className="bop-select"
                  value={form.cadenzaRata}
                  onChange={e => setForm(p => ({ ...p, cadenzaRata: e.target.value }))}
                >
                  <option value="MENSILE">Mensile</option>
                  <option value="TRIMESTRALE">Trimestrale</option>
                  <option value="SEMESTRALE">Semestrale</option>
                  <option value="ANNUALE">Annuale</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 2: Opzioni avanzate ── */}
        {form.step === 2 && (
          <div className="bop-finanz__vstack--lg">
            <div className="bop-finanz__info-box">
              <div className="bop-finanz__check-row">
                <input
                  type="checkbox"
                  checked={form.haPreammortamento}
                  onChange={e => setForm(p => ({ ...p, haPreammortamento: e.target.checked }))}
                />
                <label className="bop-finanz__check-label--md">Preammortamento</label>
              </div>
              {form.haPreammortamento && (
                <>
                  <div className="bop-finanz__mb-2">
                    <label className="bop-label">Durata preammortamento (mesi) *</label>
                    <input
                      className="bop-input"
                      type="number"
                      value={form.durataPreammortamentoMesi}
                      onChange={e => setForm(p => ({ ...p, durataPreammortamentoMesi: e.target.value }))}
                    />
                  </div>
                  <div className="bop-finanz__info-note">
                    Durante il preammortamento vengono pagate solo le quote interessi, senza ridurre il capitale.
                  </div>
                </>
              )}
            </div>

            <div className="bop-finanz__info-box">
              <div className="bop-finanz__check-row">
                <input
                  type="checkbox"
                  checked={!!form.tipoAsset}
                  onChange={e => setForm(p => ({ ...p, tipoAsset: e.target.checked ? 'ALTRO' : '' }))}
                />
                <label className="bop-finanz__check-label--md">Asset collegato (mutuo ipotecario)</label>
              </div>
              {form.tipoAsset && (
                <div className="bop-finanz__vstack">
                  <div className="bop-form-group">
                    <label className="bop-label">Tipo Asset</label>
                    <select
                      className="bop-select"
                      value={form.tipoAsset}
                      onChange={e => setForm(p => ({ ...p, tipoAsset: e.target.value }))}
                    >
                      {Object.entries(TIPO_ASSET_LABELS).map(([k, v]) =>
                        <option key={k} value={k}>{v}</option>
                      )}
                    </select>
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Descrizione Asset</label>
                    <input
                      className="bop-input"
                      placeholder="es. Capannone via Roma 10"
                      value={form.descrizioneAsset}
                      onChange={e => setForm(p => ({ ...p, descrizioneAsset: e.target.value }))}
                    />
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Valore Stimato (€)</label>
                    <input
                      className="bop-input"
                      type="number"
                      value={form.valoreStimatoAsset}
                      onChange={e => setForm(p => ({ ...p, valoreStimatoAsset: e.target.value }))}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="bop-form-group">
              <label className="bop-label">Note</label>
              <textarea
                className="bop-input"
                rows={3}
                value={form.note}
                onChange={e => setForm(p => ({ ...p, note: e.target.value }))}
              />
            </div>
          </div>
        )}

        {/* ── STEP 3: Anteprima piano ── */}
        {form.step === 3 && (
          <div className="bop-finanz__anteprima">
            <div className="bop-finanz__section-title">
              Anteprima piano (prime 12 rate)
            </div>
            {anteprima.length === 0 ? (
              <div className="bop-empty-state">Dati insufficienti per il calcolo</div>
            ) : (
              <>
                <div className="bop-table-wrapper">
                  <table className="bop-table">
                    <thead>
                      <tr>
                        <th>N.</th>
                        <th className="bop-text-right">Rata</th>
                        <th className="bop-text-right">Capitale</th>
                        <th className="bop-text-right">Interessi</th>
                        <th className="bop-text-right">Residuo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {anteprima.map(r => (
                        <tr key={r.n}>
                          <td>{r.n}</td>
                          <td className="bop-text-right">{formatCurrency(r.rata)}</td>
                          <td className="bop-text-right">{formatCurrency(r.qc)}</td>
                          <td className="bop-text-right">{formatCurrency(r.qi)}</td>
                          <td className="bop-text-right">{formatCurrency(r.residuo)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="bop-finanz__anteprima-summary">
                  Totale interessi (prime {anteprima.length} rate):{' '}
                  {formatCurrency(anteprima.reduce((s, r) => s + r.qi, 0))}
                </div>
              </>
            )}
          </div>
        )}

      </div>
    </Layout>
  );
}
